import { nextTick } from 'vue';
import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  isNavigationFailure,
  type LocationQuery,
  NavigationFailureType,
  type RouteRecordRaw,
} from 'vue-router';
import { type FeatureFlag, featureEnabled, features, type Platform } from '@/features';
import { navigationEnded, navigationStarted } from '@/lib/loadingScreen';
import useAuthStore from '@/store/modules/auth';
import useEnvStore from '@/store/modules/env';
import { internalPath } from '@/utils/routeQuery';

function redirectWithParams(name: string) {
  return ({
    params,
    query,
    hash,
  }: {
    params: Record<string, string | string[]>;
    query: LocationQuery;
    hash: string;
  }) => ({
    name,
    params,
    query,
    hash,
  });
}

const DEFAULT_TITLE: string = 'SparkLayer Dashboard';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    // Resolved to the landing page in `beforeEach`, which is the earliest the stores are loaded.
    component: () => import('@/components/SiteLoadingScreen.vue'),
    meta: { auth: true },
  },
  {
    path: '/:siteId/:siteEnv/forms',
    name: 'forms',
    component: () => import('@/layouts/AppMainV2.vue'),
    props: { fullBleed: true },
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'forms-app',
        meta: { auth: true, name: 'Forms' },
        component: () => import('@/views/FormsApp.vue'),
      },
      {
        // Catch-all for forms app internal TanStack routes.
        path: ':pathMatch(.*)*',
        name: 'forms-app-catchall',
        meta: { auth: true, name: 'Forms' },
        component: () => import('@/views/FormsApp.vue'),
      },
    ],
  },
  {
    path: '/getting-ready',
    name: 'Getting Ready',
    component: () => import('@/views/GettingReady.vue'),
    meta: { auth: true },
  },
  {
    path: '/:siteId',
    name: 'team-redirect',
    component: () => import('@/components/SiteLoadingScreen.vue'),
    meta: { auth: true, redirectName: 'home' },
  },
  {
    path: '/:siteId/partners',
    name: 'partners',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    beforeEnter: () => {
      const envStore = useEnvStore();
      if (!envStore.isPartner) {
        return { name: 'home' };
      }
      return;
    },
    children: [
      {
        path: '',
        name: 'partners-redirect',
        redirect: redirectWithParams('partner-overview'),
      },
      {
        path: 'overview',
        name: 'partner-overview',
        component: () => import('@/views/Partners/Overview.vue'),
        meta: {
          auth: true,
          name: 'Partner Dashboard',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv',
    name: 'site',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'team-overview',
        component: () => import('@/views/HomeRouter.vue'),
        meta: { auth: true },
      },
      {
        path: 'activity',
        name: 'activity',
        component: () => import('@/views/SalesActivity/ActivityV2.vue'),
        meta: {
          auth: true,
          name: 'Activity',
        },
      },
      {
        path: 'features',
        name: 'features',
        component: () => import('@/views/Features/FeaturesV2.vue'),
        meta: {
          auth: true,
          name: 'Explore Features',
        },
      },
      {
        path: 'sales-agents',
        name: 'sales-agents',
        component: () => import('@/views/SalesAgents/SalesAgents.vue'),
        meta: {
          auth: true,
          name: 'Sales Agents',
          redirectName: 'v2-sales-agents',
          redirectFeature: features.CUSTOMERS_V2,
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/configuration',
    name: 'configuration',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'config-redirect',
        redirect: redirectWithParams('config-onboarding'),
      },
      {
        path: 'onboarding',
        name: 'config-onboarding',
        component: () => import('@/views/Settings/SettingsSetup.vue'),
        meta: {
          auth: true,
          name: 'Setup - Settings',
        },
      },
      {
        path: 'integrations',
        name: 'partner-integrations-redirect',
        redirect: redirectWithParams('integrations-platform'),
      },
      {
        path: 'integrations/platform',
        name: 'integrations-platform-redirect',
        redirect: redirectWithParams('integrations-platform'),
      },
      {
        path: 'general',
        name: 'general',
        redirect: redirectWithParams('config-general'),
        children: [
          {
            path: '',
            name: 'config-general',
            component: () => import('@/views/Settings/SettingsConfigurationsGeneral.vue'),
            meta: {
              auth: true,
              name: 'Configurations - Settings',
            },
          },
          {
            path: 'quote-statuses',
            name: 'config-general-quote-statuses',
            component: () => import('@/views/Settings/SettingsConfigurationsQuoteStatuses.vue'),
            meta: {
              auth: true,
              name: 'Quote Statuses - Settings',
            },
          },
          {
            path: 'notifications',
            name: 'config-general-notifications',
            component: () =>
              import('@/views/Settings/SettingsConfigurationsPurchaseNotifications.vue'),
            meta: {
              auth: true,
              name: 'Notifications - Settings',
            },
          },
          {
            path: 'invoices',
            name: 'config-general-invoices',
            component: () => import('@/views/Settings/SettingsConfigurationsInvoices.vue'),
            meta: {
              auth: true,
              name: 'Invoice Settings - Settings',
            },
          },
          {
            path: 'store-details',
            name: 'config-general-store-details',
            component: () => import('@/views/Settings/SettingsConfigurationsStoreDetails.vue'),
            meta: {
              auth: true,
              name: 'Store Details - Settings',
            },
          },
        ],
      },
      {
        // Legacy URLs, kept for existing bookmarks.
        path: 'settings',
        name: 'config-settings',
        redirect: redirectWithParams('config-general'),
        children: [
          {
            path: 'general',
            name: 'config-settings-general',
            redirect: redirectWithParams('config-general'),
          },
        ],
      },
      {
        path: 'api',
        name: 'api',
        meta: { auth: true },
        children: [
          {
            path: '',
            name: 'config-api',
            component: () => import('@/views/Settings/SettingsAPI.vue'),
            meta: {
              auth: true,
              name: 'API - Settings',
            },
          },
        ],
      },
      {
        path: 'shipping',
        name: 'shipping',
        meta: { auth: true },
        children: [
          {
            path: '',
            name: 'config-shipping',
            component: () => import('@/views/Settings/SettingsShippingMethods.vue'),
            meta: {
              auth: true,
              name: 'Shipping - Settings',
            },
          },
          {
            path: 'create',
            name: 'config-shipping-create',
            component: () => import('@/views/Settings/SettingsShippingMethodCreate.vue'),
            meta: {
              auth: true,
              name: 'Create Shipping Method - Settings',
            },
          },
          {
            path: 'edit/:id',
            name: 'config-shipping-edit',
            component: () => import('@/views/Settings/SettingsShippingMethodEdit.vue'),
            meta: {
              auth: true,
              name: 'Edit Shipping Method - Settings',
            },
          },
        ],
      },
      {
        path: 'theme',
        name: 'config-theme',
        component: () => import('@/views/Settings/SettingsTheme.vue'),
        meta: {
          auth: true,
          name: 'Theme - Settings',
        },
      },
      {
        // (DEV-5499) Legacy callback URL
        path: 'integrations/oauth-brightpearl',
        name: 'config-int-brightpearl',
        redirect: redirectWithParams('int-brightpearl-oauth'),
      },
      {
        // (DEV-5499) Legacy callback URL
        path: 'integrations/oauth-katana',
        name: 'config-int-katana',
        redirect: redirectWithParams('int-katana-oauth'),
      },
      {
        // (DEV-5499) Legacy callback URL
        path: 'integrations/oauth-quickbooks',
        name: 'config-int-quickbooks',
        redirect: redirectWithParams('int-quickbooks-oauth'),
      },
      {
        // (DEV-5499) Legacy callback URL
        path: 'integrations/oauth-xero',
        name: 'config-int-xero',
        redirect: redirectWithParams('int-xero-oauth'),
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/configuration/theme/editor/:themeId/:slug?',
    name: 'theme-editor-layout',
    component: () => import('@/layouts/AppMainV2.vue'),
    props: { fullBleed: true, fullHeight: true },
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'theme-editor',
        component: () => import('@/views/Settings/SettingsThemeEditor.vue'),
        meta: {
          auth: true,
          name: 'Theme Editor',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/storefront',
    name: 'storefront',
    component: () => import('@/layouts/AppMainV2.vue'),
    redirect: redirectWithParams('integrations-widgets'),
    meta: { auth: true },
    children: [
      {
        path: 'options',
        name: 'storefront-options',
        component: () => import('@/views/Storefront/Options.vue'),
        meta: {
          auth: true,
          name: 'Storefront - Options',
        },
      },
      {
        path: 'widgets',
        name: 'integrations-widgets',
        redirect: redirectWithParams('config-frontend-core'),
        meta: { auth: true },
        children: [
          {
            path: 'core',
            name: 'config-frontend-core',
            component: () => import('@/views/Storefront/Widgets.vue'),
            meta: {
              auth: true,
              name: 'Widgets (Core script) - Storefront',
            },
          },
          {
            path: 'pdp',
            name: 'config-frontend-pdp',
            component: () => import('@/views/Storefront/Widgets.vue'),
            meta: {
              auth: true,
              name: 'Widgets (Product page script) - Storefront',
            },
          },
          {
            path: 'css',
            name: 'config-frontend-css',
            component: () => import('@/views/Storefront/Widgets.vue'),
            meta: {
              auth: true,
              name: 'Widgets (Custom design) - Storefront',
            },
          },
          {
            path: 'tips',
            name: 'config-frontend-tips',
            component: () => import('@/views/Storefront/Widgets.vue'),
            meta: {
              auth: true,
              name: 'Widgets (Best-practice tips) - Storefront',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/:siteId/settings',
    name: 'settings',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('settings-account'),
        name: 'settings-redirect',
      },
      {
        path: 'account',
        name: 'settings-account',
        component: () => import('@/views/Account/SettingsAccount.vue'),
        meta: {
          auth: true,
          name: 'Account - Settings',
        },
      },
      {
        path: 'password',
        name: 'settings-password',
        component: () => import('@/views/Account/SettingsPassword.vue'),
        meta: {
          auth: true,
          name: 'Password - Settings',
        },
      },
      {
        path: 'plan',
        name: 'settings-plan',
        component: () => import('@/views/Account/SettingsBillingPlanV2.vue'),
        meta: {
          auth: true,
          name: 'Plan - Settings',
        },
      },
      {
        path: 'team',
        name: 'team',
        meta: { auth: true },
        children: [
          {
            path: '',
            name: 'settings-team',
            component: () => import('@/views/Account/SettingsTeam.vue'),
            meta: {
              auth: true,
              name: 'Team - Settings',
            },
          },
          {
            path: 'invite',
            name: 'settings-team-invite',
            component: () => import('@/views/Account/SettingsTeamInvite.vue'),
            meta: {
              auth: true,
              name: 'Invite Team Member - Settings',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/:siteId/staff',
    name: 'staff',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: {
      auth: true,
      staffOnly: true,
    },
    children: [
      {
        path: '',
        redirect: redirectWithParams('staff-manage-billing'),
        name: 'staff-redirect',
      },
      {
        path: 'manage-billing',
        name: 'staff-manage-billing',
        component: () => import('@/views/Staff/Billing/ManageBillingV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Billing',
        },
      },
      {
        path: 'manage-billing/:actionType',
        name: 'staff-manage-billing-action',
        component: () => import('@/views/Staff/Billing/ViewActionV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Billing',
        },
      },
      {
        path: 'manage-team',
        name: 'staff-manage-team',
        component: () => import('@/views/Staff/Team/ManageTeamV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Team',
        },
      },
      {
        path: 'manage-team/:actionType',
        name: 'staff-manage-team-action',
        component: () => import('@/views/Staff/Team/ViewActionV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Team',
        },
      },
      {
        path: 'customer-sync',
        name: 'staff-customer-sync',
        component: () => import('@/views/Staff/StaffCustomerSyncV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Customer Sync',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/pricing',
    name: 'products',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'products-redirect',
        redirect: redirectWithParams('pricing-lists'),
      },
      {
        path: 'lists',
        name: 'pricing-lists',
        component: () => import('@/views/Prices/PriceListsV2.vue'),
        meta: {
          auth: true,
          name: 'Price Lists',
        },
      },
      {
        path: 'lists/create',
        name: 'price-list-create',
        component: () => import('@/views/Prices/PriceListCreateV2.vue'),
        meta: {
          auth: true,
          name: 'Price List Create',
        },
      },
      {
        path: 'lists/edit/:slug',
        name: 'price-list-edit',
        component: () => import('@/views/Prices/PriceListEditV2.vue'),
        meta: {
          auth: true,
          name: 'Price List Edit',
        },
      },
      {
        path: 'editor',
        name: 'pricing-editor',
        component: () => import('@/views/Prices/PriceEditor.vue'),
        meta: {
          auth: true,
          name: 'Price Editor',
        },
      },
      {
        path: 'editor/:variantId',
        name: 'pricing-editor-variant',
        component: () => import('@/views/Prices/PriceEditorVariant.vue'),
        meta: {
          auth: true,
          name: 'Price Editor',
        },
      },
      {
        path: 'discounts',
        name: 'pricing-discounts',
        component: () => import('@/views/Prices/Discounts/List.vue'),
        meta: {
          auth: true,
          name: 'Discounts',
        },
      },
      {
        path: 'discounts/create',
        name: 'pricing-discounts-create',
        component: () => import('@/views/Prices/Discounts/Create.vue'),
        meta: {
          auth: true,
          name: 'Create Discount',
        },
      },
      {
        path: 'discounts/edit/:id',
        name: 'pricing-discounts-edit',
        component: () => import('@/views/Prices/Discounts/Edit.vue'),
        meta: {
          auth: true,
          name: 'Edit Discount',
        },
      },
    ],
  },
  {
    // Legacy Orders URL
    path: '/:siteId/:siteEnv/orders',
    name: 'orders',
    redirect: redirectWithParams('activity'),
  },
  {
    // Legacy Discounts URL
    path: '/:siteId/:siteEnv/discounts',
    name: 'discounts',
    redirect: redirectWithParams('pricing-discounts'),
  },
  {
    path: '/:siteId/:siteEnv/customers',
    name: 'customers',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'customers-redirect',
        redirect: redirectWithParams('customers-groups'),
      },
      {
        path: 'groups',
        name: 'customers-groups',
        component: () => import('@/views/Customers/CustomerGroupsV2.vue'),
        meta: {
          auth: true,
          name: 'Customer Groups',
        },
      },
      {
        path: 'groups/create',
        name: 'customers-groups-create',
        component: () => import('@/views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Create Customer Group',
        },
      },
      {
        path: 'groups/edit/:slug',
        name: 'customers-groups-edit',
        component: () => import('@/views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Customer Group',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/v2/customers',
    name: 'v2-customers',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'v2-customers-redirect',
        redirect: redirectWithParams('v2-customers-groups'),
      },
      {
        path: 'groups',
        name: 'v2-customers-groups',
        component: () => import('@/views/Customers/CustomerGroupsV2.vue'),
        meta: {
          auth: true,
          name: 'Customer Groups - Customer',
        },
      },
      {
        path: 'groups/create',
        name: 'v2-customers-groups-create',
        component: () => import('@/views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Create Customer Group',
        },
      },
      {
        path: 'groups/quick-setup',
        name: 'v2-customers-groups-quick-setup',
        component: () => import('@/views/Customers/CustomerGroupsQuickSetupV2.vue'),
        meta: {
          auth: true,
          name: 'Create Customer Group',
        },
      },
      {
        path: 'groups/edit/:slug',
        name: 'v2-customers-groups-edit',
        component: () => import('@/views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Customer Group',
        },
      },
      {
        path: 'list',
        name: 'v2-customer-list',
        component: () => import('@/views/Customers/CustomersCompanyUsersV2.vue'),
        meta: {
          auth: true,
          name: 'Customers',
        },
      },
      {
        path: 'companies',
        name: 'v2-customers-companies',
        component: () => import('@/views/Customers/CompaniesV2.vue'),
        meta: {
          auth: true,
          name: 'Companies',
        },
      },
      {
        path: 'companies/create',
        name: 'v2-customers-companies-create',
        component: () => import('@/views/Customers/CompanyFormV2.vue'),
        meta: {
          auth: true,
          name: 'Create Company',
        },
      },
      {
        path: 'companies/edit/:id',
        name: 'v2-customers-companies-edit',
        component: () => import('@/views/Customers/CompanyFormV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Company',
        },
      },
      {
        path: 'companies/:id/sections',
        name: 'v2-customers-companies-sections',
        component: () => import('@/views/Customers/SectionsV2.vue'),
        meta: {
          auth: true,
          name: 'Company Sections',
        },
      },
      {
        path: 'companies/:id/sections/create',
        name: 'v2-customers-companies-sections-create',
        component: () => import('@/views/Customers/SectionFormV2.vue'),
        meta: {
          auth: true,
          name: 'Create Section',
        },
      },
      {
        path: 'companies/:id/sections/edit/:sectionId',
        name: 'v2-customers-companies-sections-edit',
        component: () => import('@/views/Customers/SectionFormV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Section',
        },
      },
      {
        path: 'agents',
        name: 'v2-sales-agents',
        component: () => import('@/views/Customers/CustomerSalesAgentsV2.vue'),
        meta: {
          auth: true,
          name: 'Sales Agents',
        },
      },
      {
        path: 'list/details/:id',
        name: 'v2-customers-details',
        component: () => import('@/views/Customers/CustomerDetailsV2.vue'),
        meta: {
          auth: true,
          name: 'Customer Details',
        },
      },
      {
        path: 'agents/details/:id',
        name: 'v2-agents-details',
        component: () => import('@/views/Customers/CustomerDetailsV2.vue'),
        meta: {
          auth: true,
          name: 'Sales Agents Details',
        },
      },
      {
        path: 'roles',
        name: 'v2-customers-roles',
        component: () => import('@/views/Customers/CustomerRolesV2.vue'),
        meta: {
          auth: true,
          name: 'Roles',
        },
      },
      {
        path: 'roles/create',
        name: 'v2-customers-roles-create',
        component: () => import('@/views/Customers/CustomerRoleFormV2.vue'),
        meta: {
          auth: true,
          name: 'Create Role',
        },
      },
      {
        path: 'roles/edit/:slug',
        name: 'v2-customers-roles-edit',
        component: () => import('@/views/Customers/CustomerRoleFormV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Role',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/integrations',
    name: 'integrations',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'integrations-redirect',
        redirect: redirectWithParams('integrations-platform'),
      },
      {
        path: 'platform',
        name: 'integrations-platform',
        component: () => import('@/views/Integrations/Platform.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Platform',
        },
      },
      {
        path: 'platform/metafields',
        name: 'integrations-platform-metafields',
        component: () => import('@/views/Integrations/PlatformMetafields.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Platform - Metafields',
        },
      },
      {
        path: 'partners',
        name: 'integrations-partners',
        component: () => import('@/views/Integrations/PartnerIntegrations.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Partners',
        },
      },
      {
        path: 'partners/oauth/brightpearl',
        name: 'int-brightpearl-oauth',
        component: () => import('@/views/Settings/OAuth/Brightpearl.vue'),
        meta: {
          auth: true,
          name: 'Brightpearl - Integrations (Partner Integrations) - Settings',
        },
      },
      {
        path: 'partners/oauth/katana',
        name: 'int-katana-oauth',
        component: () => import('@/views/Settings/OAuth/Katana.vue'),
        meta: {
          auth: true,
          name: 'Katana - Integrations (Partner Integrations) - Settings',
        },
      },
      {
        path: 'partners/oauth/quickbooks',
        name: 'int-quickbooks-oauth',
        component: () => import('@/views/Settings/OAuth/QuickBooks.vue'),
        meta: {
          auth: true,
          name: 'QuickBooks - Integrations (Partner Integrations) - Settings',
        },
      },
      {
        path: 'partners/oauth/xero',
        name: 'int-xero-oauth',
        component: () => import('@/views/Settings/OAuth/Xero.vue'),
        meta: {
          auth: true,
          name: 'Xero - Integrations (Partner Integrations) - Settings',
        },
      },
      {
        path: 'customers-sync',
        name: 'integrations-customers-sync',
        component: () => import('@/views/Integrations/CustomersSync.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Customers Sync',
          redirectName: 'v2-customer-list',
          redirectFeature: features.CUSTOMERS_V2,
        },
      },
      {
        path: 'customers-sync/:identifier',
        name: 'integrations-customer-sync-data',
        component: () => import('@/views/Integrations/CustomerSyncData.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Customer Sync Data',
          redirectName: 'v2-customer-list',
          redirectFeature: features.CUSTOMERS_V2,
        },
      },
      {
        path: 'products-sync',
        name: 'integrations-products-sync',
        component: () => import('@/views/Integrations/ProductsSync.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Products Sync',
        },
      },
      {
        path: 'products-sync/:variantSku',
        name: 'integrations-product-data',
        component: () => import('@/views/Integrations/ProductData.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Product Data',
        },
      },
      {
        path: 'widgets',
        name: 'integrations-widgets-legacy',
        redirect: redirectWithParams('integrations-widgets'),
        meta: { auth: true },
        children: [
          {
            path: 'core',
            name: 'config-frontend-core-legacy',
            redirect: redirectWithParams('config-frontend-core'),
          },
          {
            path: 'pdp',
            name: 'config-frontend-pdp-legacy',
            redirect: redirectWithParams('config-frontend-pdp'),
          },
          {
            path: 'css',
            name: 'config-frontend-css-legacy',
            redirect: redirectWithParams('config-frontend-css'),
          },
          {
            path: 'tips',
            name: 'config-frontend-tips-legacy',
            redirect: redirectWithParams('config-frontend-tips'),
          },
        ],
      },
    ],
  },

  {
    path: '/:siteId/:siteEnv/analytics',
    name: 'analytics',
    component: () => import('@/views/Analytics/MainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'analytics-redirect',
        redirect: redirectWithParams('analytics-dash'),
      },
      {
        path: 'dashboard',
        name: 'analytics-dash',
        component: () => import('@/views/Analytics/AnalyticsDashboard.vue'),
        meta: {
          auth: true,
          name: 'Analytics',
        },
      },
      {
        path: 'chat',
        name: 'analytics-chat',
        component: () => import('@/views/Analytics/AnalyticsChat.vue'),
        meta: {
          auth: true,
          name: 'Analytics Chat',
        },
      },
      {
        path: 'report/:reportSlug',
        name: 'analytics-report',
        component: () => import('@/views/Analytics/AnalyticsReport.vue'),
        meta: {
          auth: true,
          name: 'Analytics Report',
        },
      },
      {
        path: 'custom/:customId',
        name: 'analytics-custom',
        component: () => import('@/views/Analytics/AnalyticsCustomReport.vue'),
        meta: {
          auth: true,
          name: 'Analytics Custom Report',
        },
      },
    ],
  },
  {
    path: '/help',
    component: () => import('@/layouts/AppMainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'help',
        component: () => import('@/views/Help.vue'),
        meta: {
          auth: true,
          name: 'Help',
        },
      },
    ],
  },
  {
    path: '/partner-register',
    name: 'register-partner',
    component: () => import('@/views/Partners/Register.vue'),
    meta: {
      auth: false,
      guestOnly: true,
      name: 'Partner Register',
    },
  },
  {
    path: '/register-team-member',
    name: 'register-team-member',
    component: () => import('@/views/RegisterTeamMember.vue'),
    meta: {
      auth: false,
      name: 'Register Team Member',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: {
      auth: false,
      guestOnly: true,
      name: 'Login',
    },
  },
  {
    path: '/two-factor-challenge',
    name: 'two-factor-challenge',
    component: () => import('@/views/TwoFactorChallenge.vue'),
    meta: {
      auth: false,
      name: 'Two-Factor Authentication',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/Logout.vue'),
    meta: {
      auth: true,
      name: 'Logout',
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: {
      auth: false,
      guestOnly: true,
      name: 'Register',
    },
  },
  {
    path: '/go-register',
    name: 'register-sales',
    redirect: (to) => ({
      path: '/register',
      query: {
        ...to.query,
        app_registration_source: 'sales_pipeline_tracked_link',
      },
    }),
    meta: {
      auth: false,
      name: 'Register - Sales Pipeline Redirect',
    },
  },
  {
    path: '/go-enterprise',
    name: 'register-enterprise',
    component: () => import('@/views/RegisterEnterprise.vue'),
    meta: {
      auth: false,
      guestOnly: true,
      name: 'Register - Enterprise',
    },
  },
  {
    path: '/register-invite',
    name: 'register-invite',
    component: () => import('@/views/RegisterInvite.vue'),
    meta: {
      auth: false,
      name: 'Invite to Register',
    },
  },
  {
    path: '/no-team',
    name: 'no-team',
    component: () => import('@/views/NoTeam.vue'),
    meta: {
      auth: true,
      name: 'No Team',
    },
  },
  {
    path: '/forgotten-password',
    name: 'forgotten-password',
    component: () => import('@/views/ForgottenPassword.vue'),
    meta: {
      auth: false,
      name: 'Forgotten Password',
    },
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/views/ResetPassword.vue'),
    meta: {
      auth: false,
      name: 'Reset Password',
    },
  },
  {
    path: '/switch-account/:teamId',
    name: 'switch-account',
    component: () => import('@/views/SwitchAccount.vue'),
    meta: {
      auth: true,
      name: 'Switch Account',
    },
  },
];

// Below creates extra routes for any routes with following pre-fixes:
// - /:siteId/:siteEnv
// routes are setup to redirect correctly with siteId/siteEnv in the path
const routesWithSiteIdRedirect = new Map<string, Pick<RouteRecordRaw, 'path' | 'name'>>();

const joinRoutePath = (parentPath: string, childPath: string): string => {
  if (!childPath) {
    return parentPath;
  }

  if (childPath.startsWith('/')) {
    return childPath;
  }

  return `${parentPath}/${childPath}`.replace(/\/+/g, '/');
};

const collectRoutesWithSiteIdRedirect = (
  route: RouteRecordRaw,
  currentPath: string = route.path,
): void => {
  // empty-path index children collapse onto the parent path, so skip them entirely
  if (route.path !== '' && !routesWithSiteIdRedirect.has(currentPath)) {
    // Name the index child rather than its layout parent - routing to a parent by name leaves its
    // <router-view> empty, so the page never renders.
    const name = route.children?.find((child) => child.path === '')?.name ?? route.name;

    if (name) {
      routesWithSiteIdRedirect.set(currentPath, {
        path: currentPath,
        name,
      });
    } else {
      console.error(`All routes must be setup with a route name ${currentPath}`);
    }
  }

  route.children?.forEach((childRoute) => {
    collectRoutesWithSiteIdRedirect(childRoute, joinRoutePath(currentPath, childRoute.path));
  });
};

routes
  .filter((route) => route.path.startsWith('/:siteId/'))
  .forEach((route) => {
    collectRoutesWithSiteIdRedirect(route);
  });

routes.push(
  ...Array.from(routesWithSiteIdRedirect.values()).reduce<RouteRecordRaw[]>(
    (generatedRoutes, route) => {
      const path = route.path.replace('/:siteId', '').replace('/:siteEnv', '');
      if (!path) {
        return generatedRoutes;
      }

      generatedRoutes.push({
        path,
        component: () => import('@/components/SiteLoadingScreen.vue'),
        meta: {
          // @ts-expect-error auth not expected here
          auth: true,
          _note: 'this is a generated route, see router/index.ts',
          redirectName: route.name,
        } satisfies RouteRecordRaw,
      });

      return generatedRoutes;
    },
    [],
  ),
);

// finally push the thankful 404
routes.push({
  path: '/:pathMatch(.*)*',
  name: 'four-oh-four',
  redirect: '/',
  meta: { auth: true, redirectName: 'home' },
});

// PR preview builds are served from a branch sub-path (e.g. /branch/branch-name/) on a
// static bucket that has no per-branch SPA fallback, so a hard refresh on a deep
// link would fall back to the root index.html (base "/") and parse the branch name
// as a route. Hash routing keeps the server-visible path at the deploy base and
// resolves routes client-side, so previews work on refresh without server rewrites.
// Enabled only for preview builds via the CI workflow; master/prod keep clean URLs.
const history =
  import.meta.env.VITE_PREVIEW_HASH_ROUTING === 'true'
    ? createWebHashHistory(import.meta.env.BASE_URL)
    : createWebHistory(import.meta.env.BASE_URL);

const router = createRouter({
  history,
  routes,
});

// Registered first, so a load started by the guard below knows a navigation is in flight.
router.beforeEach(navigationStarted);

router.beforeEach(async ({ meta, name, path, fullPath, params, query, hash }) => {
  const envStore = useEnvStore();
  const authStore = useAuthStore();

  const { auth, guestOnly, redirectName, staffOnly, redirectFeature } = meta as {
    auth: boolean;
    guestOnly?: boolean;
    redirectName: string;
    staffOnly: boolean;
    redirectFeature?: FeatureFlag;
  };

  await authStore.loadUser();

  const isLoggedIn = authStore.isLoggedIn;
  const currentTeam = authStore.data?.currentTeam ?? null;
  const hasTeam = currentTeam !== null;
  const isSparkStaff = authStore.isSparkStaff;

  // Redirect logged-in users away from logged out urls
  if (isLoggedIn && guestOnly) {
    // Return a string, not { path }, so the query and hash from `from` are kept
    const from = name === 'login' ? internalPath(query.from) : undefined;
    // A guest-only `from` would land straight back here, so it is never a valid target
    const isLoop =
      from !== undefined && (router.resolve(from).meta as { guestOnly?: boolean }).guestOnly;

    return from && !isLoop ? from : { name: 'home' };
  }

  // Pre-initial checks
  switch (true) {
    case !auth && !isLoggedIn:
      // If no auth is needed and no login is present continue with out any setup
      return;
    case auth && !isLoggedIn:
      // Not logged in, so push to login with the target route as from
      return {
        name: 'login',
        query: { from: fullPath },
      };
    case staffOnly && !isSparkStaff:
      return {
        name: 'home',
      };
    case path !== '/no-team' && path !== '/logout' && auth && !hasTeam:
      return { name: 'no-team' };
  }

  // Load team / team environment
  // First from the routing params if provided
  if (params?.siteId) {
    const siteId = params.siteId as string;
    // The url wins when it names an environment. Otherwise stay in the one already loaded for
    // this site, then fall back to the site's own default - partners have no live environment.
    const loadedSiteEnv = envStore.siteId === siteId ? envStore.siteEnv : null;
    const siteEnv =
      (params.siteEnv as string | undefined) ??
      loadedSiteEnv ??
      authStore.defaultSiteEnv(siteId) ??
      'live';

    await envStore.loadEnv(
      { siteId, siteEnv },
      // An environment we picked ourselves can move anywhere that loads. One the url asked for
      // may only move for a partner site, which has no live environment to ask for.
      { fallback: params.siteEnv === undefined ? 'any' : 'partner' },
    );

    // A partner site asked for its live environment loads the test one instead, so send the url
    // after it rather than leaving the two disagreeing. Nothing else reaches here: the store
    // only holds an environment other than the one asked for when the site rejected that one
    // and called itself a partner.
    if (params.siteEnv !== undefined && envStore.isLoaded && envStore.siteEnv !== params.siteEnv) {
      return {
        name,
        params: { ...params, siteEnv: envStore.siteEnv as string },
        query,
        hash,
      };
    }
  } else if (!envStore.isLoaded && currentTeam) {
    // Otherwise let's load the default for the user logged in
    await envStore.loadEnv(
      {
        siteId: currentTeam.slugged_name,
        siteEnv: authStore.defaultSiteEnv(currentTeam.slugged_name) ?? 'live',
      },
      { fallback: 'any' },
    );
  }

  const isSiteReady = envStore.isSiteReady;
  if (path !== '/getting-ready' && !isSiteReady && hasTeam) {
    return { name: 'Getting Ready' };
  }

  const siteID = envStore.env?.team.slugged_name as string;
  const platform = envStore.env?.platform as Platform;

  // Shopify users have their own onboarding flow, so prevent them from accessing the generic onboarding flow
  if (name === 'config-onboarding' && envStore.isPlatform('shopify')) {
    return { name: 'config-general', params };
  }

  // Staff whose 2FA is required but not yet enrolled get pushed to the password settings
  // page, where two-factor setup lives. This is UX only - the server middleware is the
  // real enforcement.
  //
  // `/getting-ready` and `/no-team` are exempt because the checks above own those paths: with
  // no exemption each guard redirects away from the other's destination and the navigation
  // ends in vue-router's infinite-redirect abort.
  const twoFactorExemptPaths = ['/logout', '/getting-ready', '/no-team'];
  if (
    authStore.twoFactorRequired &&
    !authStore.twoFactorEnabled &&
    !twoFactorExemptPaths.includes(path) &&
    !path.endsWith('/settings/password')
  ) {
    // `settings-password` requires a siteId, and `env` is null whenever loading it failed. Fall
    // back to the user's own team rather than passing `undefined` into a required param, which
    // vue-router rejects outright; if neither resolves, leave the navigation alone (the server
    // still enforces) instead of failing it.
    const settingsSiteId = envStore.env?.team.slugged_name ?? currentTeam?.slugged_name;

    if (settingsSiteId) {
      return {
        name: 'settings-password',
        params: { siteId: settingsSiteId },
      };
    }
  }

  // `/` names no site, so the landing page is resolved here rather than by a route-level
  // `redirect` - those resolve before the guards run, when the stores are still empty.
  if (name === 'home') {
    // Partners have their own overview, and no live environment to land in.
    if (currentTeam?.is_partner) {
      return {
        name: 'partner-overview',
        params: { siteId: currentTeam.slugged_name },
        query,
        hash,
      };
    }

    // `env` holds a site id and its environment together, so the two only ever come from one team.
    const landingSiteId = envStore.siteId ?? currentTeam?.slugged_name;

    if (landingSiteId) {
      return {
        name: 'team-overview',
        params: {
          siteId: landingSiteId,
          siteEnv: envStore.siteEnv ?? authStore.defaultSiteEnv(landingSiteId) ?? 'live',
        },
        query,
        hash,
      };
    }
  }

  // Redirect route?
  if (redirectName) {
    if (!redirectFeature || featureEnabled(redirectFeature, siteID, platform)) {
      // Keep the rest of the target's params, or a route missing one fails or inherits the wrong value
      return {
        name: redirectName,
        params: {
          ...params,
          siteId: siteID,
          siteEnv: envStore.env?.environment as string,
        },
        query,
        hash,
      };
    }
  }
});

router.onError(navigationEnded);

router.afterEach(({ path, meta }, _from, failure) => {
  // A cancelled navigation was replaced by a newer one that is still in flight, and which ends
  // the run of loading screens itself.
  if (!isNavigationFailure(failure, NavigationFailureType.cancelled)) {
    navigationEnded();
  }

  window.Intercom('update');
  window._hsq = window._hsq || [];
  const _hsq = window._hsq;
  _hsq.push(['setPath', path]);
  _hsq.push(['trackPageView']);

  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    document.title = meta?.name ? `${meta.name} - ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  });
});

export default router;
